import React, { useMemo, useState, useEffect, useRef } from 'react';
import { columnTypes, DataTable, MultiSelect, dateTranslate } from 'yankees-react-utils';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getSpeedColor, useFormatSpeed } from './utils';
import VideoPlayerButton from '../../../components/VideoPlayerButton';

const PlayLogWrapper = styled.div`
    .opacity-25 {
        opacity: 0.25;
    }
`;

const playTypeOptions = ['Stolen Base Attempt', 'Baserunner on Batted Ball', 'Baserunner in Motion on Batted Ball',
    'Baserunner Other', 'Batter on Batted Ball', 'Batter Other', 'Fielder on Batted Ball', 'Fielder Other'].map((x) => ({ name: x, value: x }));

const PlayLog = ({ className, inGameSpeeds, maxSpeed, showMph }) => {
    const modalContainer = useRef();
    const { t } = useTranslation();
    const { formatSpeed } = useFormatSpeed();

    const [overrideAngleKey, setOverrideAngleKey] = useState('');

    const [playLogRangeFilters, setPlayLogRangeFilters] = useState([
        { lowerLabel: 88, upperLabel: 100, lowerLimit: 88, color: '#FF0000', enabled: true, rows: [] },
        { lowerLabel: 78, upperLabel: 88, lowerLimit: 78, upperLimit: 88, color: '#254C75', enabled: true, rows: [] },
        { lowerLabel: 65, upperLabel: 78, upperLimit: 78, color: '#0077D9', enabled: true, rows: [] },
    ]);
    const [playTypes, setPlayTypes] = useState([]);

    const onPlaylistFetched = (pl) => {
        const angles = ['MLBAM High Home', 'Field High', 'Field Low', 'Main'];
        angles.every((a) => {
            if (pl?.[0].sources.some((s) => s.label === a)) {
                setOverrideAngleKey(a);
                return false;
            }
            return true;
        });
    };

    const columns = useMemo(() => [{
        id: 'video',
        orderable: false,
        type: columnTypes.NODE,
        transform: (r) => (
            <VideoPlayerButton
                modalContainer={modalContainer}
                gameid={r.gameid}
                eventseq={r.eventseq}
                pitchseq={r.pitchseq}
                useBASEIcon={false}
                overrideAngleKey={overrideAngleKey}
                onPlaylistSearchSuccess={onPlaylistFetched}
            />
        ),
        headerClass: 'text-center',
        cellClass: 'text-center',
    }, {
        id: 'gamedate',
        title: t('sections.post-game.in-game-speed.date'),
        transform: (r) => dateTranslate(t, `${moment(r.gamedate).format('M/D/Y')} 12:00:00`),
        type: columnTypes.TEXT,
        headerClass: 'text-center',
        cellClass: 'text-center',
        sortBy: (r) => r.gamedate,
    }, {
        id: 'opp',
        title: t('sections.post-game.in-game-speed.opp'),
        transform: (r) => `${r.playerteamid === r.hometeamid ? 'vs.' : '@'} ${r.playerteamid === r.hometeamid ? r.awayteamabbr : r.hometeamabbr}`,
        type: columnTypes.TEXT,
        headerClass: 'text-center',
        cellClass: 'text-center text-nowrap px-2',
        sortBy: (r) => (r.playerteamid === r.hometeamid ? r.awayteamabbr : r.hometeamabbr),
    }, {
        id: 'inning',
        title: t('sections.post-game.in-game-speed.inning'),
    }, {
        id: 'positionabbr',
        title: t('sections.post-game.in-game-speed.position'),
        type: columnTypes.TEXT,
        headerClass: 'text-center',
        cellClass: 'text-center',
        sortBy: (r) => r.position,
    }, {
        id: 'playtype',
        title: t('sections.post-game.in-game-speed.play-type'),
        type: columnTypes.TEXT,
    }, {
        id: 'description',
        title: t('sections.post-game.in-game-speed.play-description'),
        type: columnTypes.TEXT,
    }, {
        id: 'speed',
        title: t('sections.post-game.in-game-speed.speed'),
        type: columnTypes.TEXT,
        transform: (r) => <span style={{ color: r.isflagged ? undefined : getSpeedColor(r.maxspeed / maxSpeed) }}>{formatSpeed(r.maxspeed, showMph)}</span>,
        headerClass: 'text-center',
        cellClass: 'text-center text-nowrap px-2',
        sortBy: (r) => r.maxspeed,
    }, {
        id: 'speedpercent',
        title: t('sections.post-game.in-game-speed.percent-of-max-speed'),
        type: columnTypes.TEXT,
        headerClass: 'text-center text-nowrap',
        cellClass: 'text-center text-nowrap',
        transform: (r) => (r.maxspeed && maxSpeed ? `${(r.maxspeed / maxSpeed * 100).toString().split('.')[0]}%` : '-'),
        sortBy: (r) => r.maxspeed,
    }], [maxSpeed, showMph, formatSpeed, t, overrideAngleKey]);

    const filteredPlayTypes = useMemo(() => inGameSpeeds.filter((s) => !playTypes.length || playTypes.includes(s.playtype)), [inGameSpeeds, playTypes]);

    useEffect(() => {
        setPlayLogRangeFilters(playLogRangeFilters.map((plrf) => ({
            ...plrf,
            rows: filteredPlayTypes.filter((s) => (!plrf.lowerLimit || (s.maxspeed / maxSpeed * 100 >= plrf.lowerLimit))
                && (!plrf.upperLimit || (s.maxspeed / maxSpeed * 100 <= plrf.upperLimit))),
        })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inGameSpeeds, maxSpeed, filteredPlayTypes]);

    const handleFilterClick = (plrf) => {
        setPlayLogRangeFilters(playLogRangeFilters.map((p) => (p.upperLabel === plrf.upperLabel ? ({ ...p, enabled: !p.enabled }) : p)));
    };

    const filteredRows = playLogRangeFilters.reduce((all, x) => all.concat(
        x.enabled ? x.rows : [],
    ), []);

    return (
        <PlayLogWrapper className={`bg-white border rounded py-3 px-4 ${className}`} ref={modalContainer}>
            <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <h4 className="mb-0 me-4">{t('sections.post-game.in-game-speed.play-log')}</h4>
                    {playLogRangeFilters.map((plrf) => (
                        <div
                            className="border rounded py-1 px-2 fs-6 text-muted me-2"
                            style={{ backgroundColor: plrf.enabled ? 'white' : '#eeeeee', cursor: 'pointer' }}
                            onClick={() => handleFilterClick(plrf)}
                            key={plrf.upperLabel}
                        >
                            <span
                                className="fw-bolder"
                                style={{ color: plrf.enabled ? plrf.color : '#8f8f8f' }}
                            >
                                {plrf.lowerLabel}-{plrf.upperLabel}%
                            </span> ({plrf.rows.length})
                        </div>
                    ))}
                    <span className="ms-2">{t('sections.post-game.in-game-speed.play-types')}:&nbsp;</span>
                    <MultiSelect options={playTypeOptions} value={playTypes} setValue={setPlayTypes} canExclusiveSelect />
                </div>
            </div>
            {filteredRows.length ? (
                <DataTable
                    className="mt-3"
                    columns={columns}
                    rows={filteredRows}
                    sortable={{ id: 'speed', isDesc: true }}
                />
            ) : <div className="d-flex justify-content-center py-5 text-muted fs-6">{t('sections.post-game.in-game-speed.no-data')}</div>}
        </PlayLogWrapper>
    );
};

export default PlayLog;
